// frontend/src/components/report/LegalTab.js
import { useState } from "react";
import { ShieldCheck, ShieldAlert, Scale, Link2, Image as ImageIcon, ChevronLeft, ChevronRight, Info, ImageOff, CheckCircle2, XCircle } from "lucide-react";
import { API } from "@/lib/api";
import { RadialScore } from "./primitives/RadialScore";
import { ReportCard } from "./primitives/ReportCard";
import { scoreTone, SEVERITY_CLS } from "./reportUtils";

// Fixed checklist categories for the paid report's legal section (owner
// request 2026-10-01): "a website in Germany should have X" — one row per
// category, green when no matching issue was found, red (with each
// matching issue's evidence + fix, both already translated server-side by
// audit/i18n_de.py's translate_finding) when at least one was. A code that
// doesn't fall into any category still surfaces, in a catch-all "other" row
// — silently dropping a real finding would be worse than an ungrouped one.
const IMPRESSUM_CODES = [
  "no_impressum", "impressum_no_contact", "impressum_no_ustid", "impressum_no_address",
  "impressum_no_representative", "impressum_no_register", "impressum_outdated_tmg", "impressum_link_label",
];
const DATENSCHUTZ_CODES = ["no_datenschutz", "datenschutz_thin", "datenschutz_missing_tool", "form_without_privacy_link", "outdated_odr_link"];
const CONSENT_CODES = ["tracking_no_cmp", "tracking_not_blocked", "google_fonts_remote", "youtube_tracking_embed", "maps_embed_no_consent", "third_party_embed_no_consent"];
const IMAGE_CODES = ["stock_images_detected"];
const AGB_CODES = ["no_agb"];

export function buildLegalChecklist(legal, issues, L) {
  if (!legal) return [];
  const C = L.legalChecklist;
  // A platform profile (Instagram/Facebook page etc. as the "website") has
  // no legal texts or scripts of its own to check — audit/legal.py returns
  // exactly one no_own_website issue and nothing else in that case.
  if (legal.platform_hosted) {
    return [{ key: "own_site", title: C.ownSite, ok: false, issues: issues.filter((f) => f.code === "no_own_website") }];
  }
  const byCode = (codes) => issues.filter((f) => codes.includes(f.code));
  const rows = [
    { key: "impressum", title: C.impressum, okText: C.impressumOk, issues: byCode(IMPRESSUM_CODES) },
    { key: "datenschutz", title: C.datenschutz, okText: C.datenschutzOk, issues: byCode(DATENSCHUTZ_CODES) },
    { key: "consent", title: C.consent, okText: C.consentOk, issues: byCode(CONSENT_CODES) },
    { key: "images", title: C.images, okText: C.imagesOk, issues: byCode(IMAGE_CODES) },
  ];
  // AGB/Widerrufsbelehrung only applies to a site that actually sells
  // online — not a requirement for e.g. a local craftsman's site, so it's
  // left out entirely rather than shown as a false "missing" row.
  if (legal.sells_online) {
    rows.push({ key: "agb", title: C.agb, okText: C.agbOk, issues: byCode(AGB_CODES) });
  }
  const categorized = new Set([...IMPRESSUM_CODES, ...DATENSCHUTZ_CODES, ...CONSENT_CODES, ...IMAGE_CODES, ...AGB_CODES]);
  const leftover = issues.filter((f) => !categorized.has(f.code) && f.code !== "no_own_website");
  if (leftover.length) rows.push({ key: "other", title: C.other, okText: "", issues: leftover });
  return rows.map((r) => ({ ...r, ok: r.issues.length === 0 }));
}

// Paginated link list for a legal finding's `data.images` (currently just
// stock_images_detected) — a flat list of every flagged image with a link,
// paged rather than truncated so a site with many stock photos doesn't get
// an arbitrarily short "top 5" that hides most of the evidence.
const PAGE_SIZE = 5;

function ImageEvidenceList({ images, originByUrl = {}, L }) {
  const [page, setPage] = useState(0);
  if (!images?.length) return null;
  const pageCount = Math.ceil(images.length / PAGE_SIZE);
  const pageStart = page * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  return (
    <div className="mt-2.5 rounded-lg border border-black/10 bg-white/50 p-2.5">
      {/* Every image stays in the DOM (not sliced to the current page) so
          "Als PDF speichern" — a plain window.print(), pagination clicks
          are impossible on paper — includes all of them; `hidden` only
          takes an item off the SCREEN page, `print:block` puts every item
          back for the printed/PDF output regardless of which page was
          open when printing. */}
      <ul className="space-y-2">
        {images.map((img, i) => {
          const origin = originByUrl[img.url];
          const externalPages = origin?.external_pages || [];
          const onCurrentPage = i >= pageStart && i < pageEnd;
          return (
            <li key={i} className={`text-sm print:block ${onCurrentPage ? "" : "hidden"}`}>
              <div className="flex items-center gap-1.5">
                <ImageIcon className="h-3 w-3 shrink-0 opacity-60" />
                <a href={img.url} target="_blank" rel="noreferrer" className="truncate hover:underline" title={img.url}>{img.url}</a>
              </div>
              {/* Confirmed-elsewhere evidence — much stronger than the
                  filename guess alone: this image was found, verbatim, on
                  specific other sites unconnected to the business. */}
              {origin?.found_elsewhere && (
                <div className="ml-4 mt-1 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-sm text-red-900">
                  <strong>{L.imgConfirmed}</strong> {L.imgFoundElsewhere(externalPages.length)}{" "}
                  {externalPages.slice(0, 4).map((p, j) => (
                    <span key={j}>{j > 0 && ", "}<a href={p.url} target="_blank" rel="noreferrer" className="underline">{p.domain}</a></span>
                  ))}
                  {externalPages.length > 4 && ` ${L.imgMore(externalPages.length - 4)}`}.
                </div>
              )}
              {origin && !origin.found_elsewhere && !origin.error && <div className="ml-4 mt-1 text-sm text-muted-foreground">{L.imgNoMatches}</div>}
            </li>
          );
        })}
      </ul>
      {pageCount > 1 && (
        <div className="mt-2 flex items-center justify-between border-t border-black/10 pt-2 text-sm print:hidden">
          <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="flex items-center gap-0.5 disabled:opacity-30"><ChevronLeft className="h-3.5 w-3.5" /> {L.back}</button>
          <span className="opacity-70">{page + 1} / {pageCount} ({L.imagesCount(images.length)})</span>
          <button type="button" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page === pageCount - 1} className="flex items-center gap-0.5 disabled:opacity-30">{L.next} <ChevronRight className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}

// Reverse-image evidence for the business's OWN Google Business Profile
// photos (user request 2026-09-08: "web sitesi ve GBP'deki fotoları analiz
// edip hangisinde telif kontrolü olmalı raporda söyleyebiliyor muyuz") —
// only the flagged (found_elsewhere) ones are passed in, already filtered
// by the caller. Shows the actual photo (via the report's gbp-photo proxy,
// server.py) rather than just an opaque Places photo id, so the owner can
// recognize which picture is meant.
function GbpPhotoEvidence({ flagged, token, L }) {
  return (
    <div className="space-y-2.5">
      {flagged.map((c, i) => (
        <div key={i} className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-2.5">
          <img src={`${API}/report/${token}/gbp-photo?ref=${encodeURIComponent(c.photo_ref)}`} alt="" className="h-16 w-16 shrink-0 rounded-md object-cover" />
          <div className="text-sm text-red-900">
            <strong>{L.imgConfirmed}</strong> {L.imgFoundElsewhere(c.external_pages.length)}{" "}
            {c.external_pages.slice(0, 4).map((p, j) => (
              <span key={j}>{j > 0 && ", "}<a href={p.url} target="_blank" rel="noreferrer" className="underline">{p.domain}</a></span>
            ))}
            {c.external_pages.length > 4 && ` ${L.imgMore(c.external_pages.length - 4)}`}.
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LegalTab({ data, L, token }) {
  const nap = data.nap_check;
  const isDetailed = !!data.is_detailed;
  const legalIssues = data.legal?.issues || [];
  const linkStructureIssues = data.link_structure?.issues || [];
  const imageOriginByUrl = Array.isArray(data.image_origin_checks)
    ? Object.fromEntries(data.image_origin_checks.map((c) => [c.image_url, c]))
    : {};
  const gbpImageChecks = Array.isArray(data.gbp_image_origin_checks) ? data.gbp_image_origin_checks : [];
  const gbpImageFlagged = gbpImageChecks.filter((c) => c.found_elsewhere);

  // Silence isn't proof of absence: a GBP-photo check that ran and found
  // nothing must say so explicitly, not just disappear — same rule the
  // website's own image check follows via the checklist's "images" row
  // below (green + okText when nothing was flagged).
  const gbpImagesChecked = isDetailed && gbpImageChecks.length > 0 && gbpImageFlagged.length === 0;
  const legalChecklist = isDetailed ? buildLegalChecklist(data.legal, legalIssues, L) : [];

  const hasAnything =
    (nap && nap.score != null) || legalChecklist.length > 0 || linkStructureIssues.length > 0 ||
    (!isDetailed && data.legal_teaser?.issue_count > 0) || (!isDetailed && data.link_structure_teaser) ||
    gbpImageFlagged.length > 0 || gbpImagesChecked;

  return (
    <>
      {/* NAP consistency: does Name/Address/Phone match across the
          directories you're listed on? Summary shows only the score;
          per-platform mismatches are a detailed-report-only reveal. */}
      {nap && nap.score != null && (
        <ReportCard icon={nap.score >= 80 ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />} title={L.napTitle} description={L.napDesc}>
          <div className="flex items-center gap-4">
            <RadialScore value={nap.score} max={100} size={72} strokeWidth={8} color={scoreTone(nap.score).bar}>
              <span className="text-lg font-bold tabular-nums" style={{ color: scoreTone(nap.score).fg }}>{nap.score}</span>
            </RadialScore>
            <p className="text-base text-muted-foreground">{L.napSummary(nap.consistent, nap.checked, !!nap.platforms)}</p>
          </div>
          {nap.platforms && (
            <div className="mt-3 space-y-1.5">
              {nap.platforms.filter((p) => p.status !== "unreachable").map((p, i) => (
                <div key={i} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${p.status === "match" ? "border-green-200 bg-green-50 text-green-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
                  <a href={p.url} target="_blank" rel="noreferrer" className="font-medium hover:underline">{p.label}</a>
                  <span>{L.napStatus[p.status] || L.napStatus.mismatch}</span>
                </div>
              ))}
            </div>
          )}
        </ReportCard>
      )}

      {/* Free-tier legal teaser: count only, same withholding pattern as
          the pillar checklist above — see server.py's legal_teaser. This
          is what makes an outreach message ("we found N legal points to
          check") honest: the free report the prospect opens actually
          shows that count, never a bare claim with nothing behind it. */}
      {!isDetailed && data.legal_teaser && data.legal_teaser.issue_count > 0 && (
        <div className="mt-6 overflow-hidden rounded-2xl border border-amber-300 bg-card shadow-sm">
          <div className="p-5 sm:p-6 bg-amber-50">
            <h2 className="mb-1.5 flex items-center gap-1.5 font-heading text-lg font-semibold text-amber-900"><Scale className="h-4 w-4" /> {L.legalTitle}</h2>
            <p className="text-base text-amber-900/80">{L.legalTeaser(data.legal_teaser.issue_count, data.legal_teaser.has_image_license_flag)}</p>
          </div>
        </div>
      )}

      {/* Legal checklist — deliberately its own block, not folded into the
          100-point score (see memory/PRD.md §12). Every category always
          shows (owner request 2026-10-01): green when clean, red with each
          issue's evidence + fix (already translated server-side) when not.
          The disclaimer is part of the feature: we measure what is
          observable in the HTML, we do not give legal advice. */}
      {legalChecklist.length > 0 && (
        <ReportCard icon={<Scale className="h-4 w-4" />} title={L.legalChecklistTitle} description={L.legalChecklistIntro}>
          <div className="space-y-2.5">
            {legalChecklist.map((row) => (
              <div
                key={row.key}
                className={`rounded-xl border p-3.5 ${row.ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}
              >
                <div className="flex items-center gap-2">
                  {row.ok ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-green-700" />
                  ) : (
                    <XCircle className="h-4 w-4 shrink-0 text-red-700" />
                  )}
                  <span className={`text-base font-semibold ${row.ok ? "text-green-900" : "text-red-900"}`}>{row.title}</span>
                </div>
                {row.ok ? (
                  <p className="mt-1 text-sm text-green-900/80">{row.okText}</p>
                ) : (
                  <div className="mt-1.5 space-y-2.5">
                    {row.issues.map((f, i) => (
                      <div key={i}>
                        <p className="text-sm text-red-900/90">{f.evidence}</p>
                        {f.fix && <p className="mt-0.5 text-sm font-medium text-red-900">→ {f.fix}</p>}
                        {f.code === "stock_images_detected" && <ImageEvidenceList images={f.data?.images} originByUrl={imageOriginByUrl} L={L} />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="mt-4 flex items-start gap-2 rounded-lg bg-secondary/60 p-3 text-sm text-muted-foreground"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{L.legalDisclaimer}</span></p>
        </ReportCard>
      )}

      {/* Free-tier link-structure teaser: counts only, same withholding
          pattern as legal_teaser above — but shows BOTH directions (user
          request 2026-09-04: "olumlu olumsuz"), since a well-linked site
          should see that confirmed, not just silence. */}
      {!isDetailed && data.link_structure_teaser && (
        <ReportCard icon={<Link2 className="h-4 w-4" />} title={L.linkStructureTitle} description={L.linkStructureTeaser(data.link_structure_teaser.positive_count, data.link_structure_teaser.issue_count)} />
      )}

      {/* Link architecture — its own block, not part of the 100-point
          score (see audit/link_structure.py). Positive and negative
          findings share one list and one styling lookup (SEVERITY now
          includes "positive"). */}
      {linkStructureIssues.length > 0 && (
        <ReportCard variant="accent" icon={<Link2 className="h-4 w-4" />} title={L.linkStructureTitle} description={L.linkStructureDesc}>
          {linkStructureIssues.map((f, i) => (
            <div key={i} className={`rounded-xl border p-3.5 ${SEVERITY_CLS[f.severity] || SEVERITY_CLS.low}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border bg-white/60 px-2 py-0.5 text-xs font-semibold">{L.severity[f.severity] || L.severity.low}</span>
                <span className="text-base font-semibold">{f.title}</span>
              </div>
              <p className="mt-1.5 text-base opacity-90">{f.evidence}</p>
            </div>
          ))}
        </ReportCard>
      )}

      {/* Google Business Profile photo copyright check — separate from
          the website's stock-image finding above since it has no
          filename to pre-filter on and comes from a direct reverse-image
          check on the business's own uploaded GBP photos (see
          vision_service.py + server.py's gather_wide_report_data). Only
          rendered when at least one photo was actually confirmed
          elsewhere — user request 2026-09-08. */}
      {gbpImageFlagged.length > 0 && (
        <ReportCard variant="accent" icon={<ImageIcon className="h-4 w-4" />} title={L.gbpPhotosTitle} description={L.gbpPhotosDesc}>
          <GbpPhotoEvidence flagged={gbpImageFlagged} token={token} L={L} />
        </ReportCard>
      )}

      {/* Same "checked and clean" honesty as the checklist's "images" row
          above, for the reverse-image search that runs directly on every
          owner-uploaded GBP photo (no filename pre-filter possible there). */}
      {gbpImagesChecked && (
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50/60 p-5 shadow-sm sm:p-6">
          <ImageOff className="mt-0.5 h-4 w-4 shrink-0 text-green-700" />
          <div>
            <h3 className="text-base font-semibold text-green-900">{L.gbpImagesCleanTitle}</h3>
            <p className="mt-1 text-sm text-green-900/80">{L.gbpImagesCleanDesc(gbpImageChecks.length)}</p>
          </div>
        </div>
      )}

      {!hasAnything && <div className="rounded-2xl border bg-card p-5 text-base text-muted-foreground shadow-sm sm:p-6">{L.noDataYet}</div>}
    </>
  );
}
